// src/contexts/NotificationContext.jsx - FIXED WITH FALLBACK QUERY
import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  collection, 
  query, 
  where, 
  onSnapshot, 
  orderBy, 
  limit, 
  doc, 
  updateDoc, 
  writeBatch, 
  serverTimestamp,
  addDoc 
} from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from './AuthContext';

const NotificationContext = createContext();

export function useNotification() {
  return useContext(NotificationContext);
}

export function NotificationProvider({ children }) {
  const { currentUser, vendorProfile } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [alert, setAlert] = useState({ show: false, message: '', type: '' });
  const [productRequestsCount, setProductRequestsCount] = useState(0);
  const [productRequests, setProductRequests] = useState([]);

  // Check if vendor has order management
  const hasOrderManagement = vendorProfile?.businessType !== 'without-orders';

  // Listen for notifications in Firestore
  useEffect(() => {
    if (!currentUser) {
      setNotifications([]);
      setUnreadCount(0);
      setProductRequestsCount(0);
      setProductRequests([]);
      return;
    }

    console.log('🔔 Setting up notification listener for vendor:', currentUser.uid);

    // Query notifications for current vendor
    const notificationRef = collection(db, 'notifications');
    const q = query(
      notificationRef, 
      where('vendorId', '==', currentUser.uid),
      orderBy('createdAt', 'desc'),
      limit(50)
    );

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      console.log('📱 Notification snapshot received:', querySnapshot.size, 'notifications');
      
      const notificationList = [];
      let unread = 0;
      let productRequestsUnread = 0;
      
      querySnapshot.forEach((doc) => {
        const notification = {
          id: doc.id,
          ...doc.data()
        };
        
        notificationList.push(notification);
        
        if (!notification.read) {
          unread++;
          
          // Count unread product request notifications
          if (notification.type === 'product_request' || notification.type === 'product-request') {
            productRequestsUnread++;
          }
        }
      });
      
      console.log('📊 Notification stats:', {
        total: notificationList.length,
        unread: unread,
        productRequests: productRequestsUnread
      });
      
      setNotifications(notificationList);
      setUnreadCount(unread);
      setProductRequestsCount(productRequestsUnread);
    }, (error) => {
      console.error('❌ Error in notification listener:', error);
    });

    return unsubscribe;
  }, [currentUser]);

  // FIXED: Real-time listener for product requests with fallback query
  useEffect(() => {
    if (!currentUser?.uid || !hasOrderManagement) {
      setProductRequests([]);
      return;
    }

    console.log('🛍️ Setting up REAL-TIME product requests listener for vendor:', currentUser.uid);

    const productRequestsRef = collection(db, 'productRequests');
    
    // First try with orderBy
    const q = query(
      productRequestsRef,
      where('vendorId', '==', currentUser.uid),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      console.log('📦 Product requests snapshot received:', snapshot.size, 'requests');
      
      const requestsList = [];
      let pendingCount = 0;
      
      snapshot.forEach((doc) => {
        const requestData = {
          id: doc.id,
          ...doc.data()
        };
        requestsList.push(requestData);
        
        // Count pending requests that aren't expired
        if (requestData.status === 'pending' && !isExpired(requestData)) {
          pendingCount++;
        }
      });

      // Sort by creation date (newest first)
      requestsList.sort((a, b) => {
        const dateA = a.createdAt?.toDate() || new Date(0);
        const dateB = b.createdAt?.toDate() || new Date(0);
        return dateB.getTime() - dateA.getTime();
      });

      console.log('📊 Product requests stats:', {
        total: requestsList.length,
        pending: pendingCount
      });
      
      setProductRequests(requestsList);
      
      // Handle new product requests (create notifications)
      snapshot.docChanges().forEach((change) => {
        if (change.type === 'added') {
          const requestData = change.doc.data();
          const requestId = change.doc.id;
          
          console.log('🆕 New product request detected:', requestData);
          
          // Check if notification already exists for this request
          const existingNotification = notifications.find(n => 
            (n.type === 'product_request' || n.type === 'product-request') && 
            n.productRequestId === requestId
          );
          
          if (!existingNotification) {
            console.log('🔔 Creating notification for new product request');
            createProductRequestNotification(requestData, requestId);
            
            // Show real-time alert
            showAlert(
              `New product request from ${requestData.mechanicName || 'a mechanic'} for ${requestData.productName || 'a product'}`,
              'info'
            );
          }
        }
        
        if (change.type === 'modified') {
          console.log('📝 Product request updated:', change.doc.id);
        }
      });
    }, (error) => {
      console.error('❌ Error in product requests listener:', error);
      
      // FALLBACK: Use query without orderBy if index is missing
      console.log('🔄 Trying fallback query without orderBy...');
      
      const fallbackQ = query(
        productRequestsRef,
        where('vendorId', '==', currentUser.uid)
      );
      
      return onSnapshot(fallbackQ, (snapshot) => {
        console.log('📦 Fallback product requests snapshot received:', snapshot.size, 'requests');
        
        const requestsList = [];
        let pendingCount = 0;
        
        snapshot.forEach((doc) => {
          const requestData = {
            id: doc.id,
            ...doc.data()
          };
          requestsList.push(requestData);
          
          // Count pending requests that aren't expired
          if (requestData.status === 'pending' && !isExpired(requestData)) {
            pendingCount++;
          }
        });

        // Sort manually by creation date (newest first)
        requestsList.sort((a, b) => {
          const dateA = a.createdAt?.toDate() || new Date(0);
          const dateB = b.createdAt?.toDate() || new Date(0);
          return dateB.getTime() - dateA.getTime();
        });

        console.log('📊 Fallback product requests stats:', {
          total: requestsList.length,
          pending: pendingCount
        });
        
        setProductRequests(requestsList);
        
        // Handle new product requests (create notifications)
        snapshot.docChanges().forEach((change) => {
          if (change.type === 'added') {
            const requestData = change.doc.data();
            const requestId = change.doc.id;
            
            console.log('🆕 New product request detected (fallback):', requestData);
            
            // Check if notification already exists for this request
            const existingNotification = notifications.find(n => 
              (n.type === 'product_request' || n.type === 'product-request') && 
              n.productRequestId === requestId
            );
            
            if (!existingNotification) {
              console.log('🔔 Creating notification for new product request (fallback)');
              createProductRequestNotification(requestData, requestId);
              
              // Show real-time alert
              showAlert(
                `New product request from ${requestData.mechanicName || 'a mechanic'} for ${requestData.productName || 'a product'}`,
                'info'
              );
            }
          }
          
          if (change.type === 'modified') {
            console.log('📝 Product request updated (fallback):', change.doc.id);
          }
        });
      }, (fallbackError) => {
        console.error('❌ Fallback query also failed:', fallbackError);
        // Set empty array if both queries fail
        setProductRequests([]);
      });
    });

    return () => {
      console.log('🔕 Cleaning up product requests listener');
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [currentUser?.uid, hasOrderManagement, notifications]);

  // Check if request is expired
  const isExpired = (request) => {
    if (!request.expiresAt) return false;
    
    try {
      const now = new Date();
      const expiry = request.expiresAt.toDate ? request.expiresAt.toDate() : new Date(request.expiresAt);
      return now > expiry;
    } catch (error) {
      return false;
    }
  };

  // Create notification for product request
  const createProductRequestNotification = async (requestData, requestId) => {
    try {
      const notificationRef = collection(db, 'notifications');
      
      // Format the notification data
      const notificationDoc = {
        vendorId: currentUser.uid,
        type: 'product_request',
        title: 'New Product Request',
        message: `${requestData.mechanicName || 'A mechanic'} requested ${requestData.requestedQuantity || 1} x ${requestData.productName || 'product'}`,
        productRequestId: requestId,
        productName: requestData.productName,
        mechanicName: requestData.mechanicName,
        mechanicId: requestData.mechanicId,
        requestedQuantity: requestData.requestedQuantity,
        read: false,
        createdAt: serverTimestamp(),
        expiresAt: requestData.expiresAt,
        // Navigation data
        orderId: null,
        chatId: null,
        productId: requestData.productId,
        customerId: requestData.mechanicId
      };

      await addDoc(notificationRef, notificationDoc);
      console.log('✅ Product request notification created');
    } catch (error) {
      console.error('❌ Error creating product request notification:', error);
    }
  };

  // Function to show alert
  const showAlert = (message, type = 'info') => {
    setAlert({
      show: true,
      message,
      type
    });

    // Auto hide after 5 seconds
    setTimeout(() => {
      setAlert((prev) => ({ ...prev, show: false }));
    }, 5000);
  };

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      const notificationRef = doc(db, 'notifications', notificationId);
      await updateDoc(notificationRef, {
        read: true,
        readAt: serverTimestamp()
      });
      
      console.log('✅ Notification marked as read:', notificationId);
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Mark all notifications as read
  const markAllAsRead = async () => {
    try {
      const batch = writeBatch(db);
      
      notifications.forEach((notification) => {
        if (!notification.read) {
          const notificationRef = doc(db, 'notifications', notification.id);
          batch.update(notificationRef, {
            read: true,
            readAt: serverTimestamp()
          });
        }
      });
      
      await batch.commit();
      console.log('✅ All notifications marked as read');
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  // Create a custom notification (for manual use)
  const createNotification = async (notificationData) => {
    try {
      const notificationRef = collection(db, 'notifications');
      const notificationDoc = {
        vendorId: currentUser.uid,
        read: false,
        createdAt: serverTimestamp(),
        ...notificationData
      };

      await addDoc(notificationRef, notificationDoc);
      console.log('✅ Custom notification created:', notificationData);
    } catch (error) {
      console.error('❌ Error creating custom notification:', error);
    }
  };

  // Get pending product requests count
  const getPendingProductRequestsCount = () => {
    return productRequests.filter(request => 
      request.status === 'pending' && !isExpired(request)
    ).length;
  };

  const value = {
    notifications,
    unreadCount,
    productRequestsCount,
    productRequests, // Add this for direct access
    getPendingProductRequestsCount,
    alert,
    showAlert,
    markAsRead,
    markAllAsRead,
    createNotification
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export default NotificationProvider;